/**
 * Stage Gate Checklist Page (formerly Opportunity Pipeline)
 * 
 * Kanban-style interface for tracking proposals through review stages.
 * Completely rewritten to replace Opportunity Pipeline functionality.
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Select,
  Input,
  useWorkspace,
  listReports,
  updateReportStatus,
  useToastStore,
  usePermissions,
  type Report,
} from '@tracekit/core';
import { StageGateKanban } from '../components/StageGateKanban';
import { StageGateProposal, StageGateFilters, StageGateStatus } from '../types/stageGate';
import { Download, Filter, RefreshCw, X, FileText } from 'lucide-react';
import { DashboardSkeleton } from '../components/LoadingSkeleton';

export function OpportunityPipelinePage() {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const { hasPermission, hasAnyPermission, isLoading: isLoadingPermissions } = usePermissions();
  const [filters, setFilters] = useState<StageGateFilters>({
    status: 'all',
    clientName: '',
    dateRange: undefined,
    reviewer: '',
  });

  // Determine user role for RBAC
  const userRole = useMemo(() => {
    if (isLoadingPermissions) return 'viewer'; // Default to most restrictive
    
    // Check for admin roles (highest priority)
    if (hasAnyPermission([
      'keystone_rfp_admin_tech',
      'admin_tech',
      'keystone_proposal_admin_tech',
      'keystone_rfp_admin',
      'admin',
      '*', // Superadmin
    ])) {
      return 'admin';
    }
    
    // Check for writer role
    if (hasAnyPermission([
      'keystone_rfp_proposal_writer',
      'proposal_writer',
      'reports.write',
    ])) {
      return 'writer';
    }
    
    // Default to viewer (most restrictive)
    return 'viewer';
  }, [hasPermission, hasAnyPermission, isLoadingPermissions]);

  // Fetch all reports (proposals)
  const { data: reports, isLoading, error, refetch } = useQuery({
    queryKey: ['stage-gate-reports', projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const reportsList = await listReports(projectId);
      console.log('[StageGateChecklist] Loaded reports:', reportsList?.length || 0);
      return reportsList;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchInterval: 30000, // Refresh every 30s
  });

  // Transform reports to StageGateProposal format
  const proposals: StageGateProposal[] = useMemo(() => {
    if (!reports) return [];

    return reports
      .filter((report: Report) => {
        // Filter for RFP proposals - handle both direct rfp_proposal type and custom with override
        const metadata = report.metadata || {};
        const isRfpProposal =
          report.report_type === 'rfp_proposal' ||
          (report.report_type === 'custom' &&
           (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id));

        if (!isRfpProposal) return false;

        // Apply filters
        if (filters.status && filters.status !== 'all') {
          // Map 'completed' to 'draft' for filtering
          let reportStatus = report.status;
          if (report.status === 'completed') {
            reportStatus = 'draft';
          } else if (!['draft', 'in_review', 'approved', 'submitted', 'rejected', 'archived'].includes(report.status)) {
            reportStatus = 'draft'; // Default unknown statuses to draft
          }
          if (reportStatus !== filters.status) {
            return false;
          }
        }

        if (filters.clientName) {
          const clientName = metadata.client_name || report.client_name || '';
          if (!clientName.toLowerCase().includes(filters.clientName.toLowerCase())) {
            return false;
          }
        }

        if (filters.dateRange) {
          const reportDate = new Date(report.created_at || report.updated_at || '');
          if (filters.dateRange === '7d') {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            if (reportDate < sevenDaysAgo) return false;
          } else if (filters.dateRange === '30d') {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            if (reportDate < thirtyDaysAgo) return false;
          } else if (filters.dateRange === '90d') {
            const ninetyDaysAgo = new Date();
            ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
            if (reportDate < ninetyDaysAgo) return false;
          }
        }

        if (filters.reviewer) {
          const reviewer = metadata.current_reviewer || metadata.reviewer || '';
          if (!reviewer.toLowerCase().includes(filters.reviewer.toLowerCase())) {
            return false;
          }
        }

        return true;
      })
      .map((report: Report) => {
        const metadata = report.metadata || {};
        
        // Map report status to stage gate status
        // Default to 'draft' if status is not recognized
        let stageGateStatus: StageGateStatus = 'draft';
        if (report.status === 'completed') {
          stageGateStatus = 'draft'; // Map completed to draft
        } else if (['draft', 'in_review', 'approved', 'submitted', 'rejected', 'archived'].includes(report.status)) {
          stageGateStatus = report.status as StageGateStatus;
        }
        // If status is not in the list above, it defaults to 'draft'
        
        return {
          id: report.id,
          reportId: report.id,
          title: metadata.proposal_title || report.title || report.client_name || 'Untitled Proposal',
          clientName: report.client_name || metadata.client_name || 'Unknown Client',
          deadline: metadata.deadline || metadata.submission_deadline,
          status: stageGateStatus,
          value: metadata.proposal_value || metadata.contract_value || 0,
          lastUpdated: report.updated_at || report.created_at || '',
          currentReviewer: metadata.current_reviewer || metadata.reviewer,
          commentsCount: metadata.comments_count || 0,
        };
      });
  }, [reports, filters]);

  // Status change mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ reportId, status }: { reportId: string; status: string }) =>
      updateReportStatus(reportId, status as any),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stage-gate-reports', projectId] });
      addToast({
        message: `Proposal status updated to ${variables.status}`,
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        message: error.message || 'Failed to update proposal status',
        type: 'error',
      });
    },
  });

  const handleProposalClick = (reportId: string) => {
    window.location.href = `/rfp/proposals-management?tab=list&reportId=${reportId}`;
  };

  const handleStatusChange = (reportId: string, newStatus: StageGateStatus) => {
    updateStatusMutation.mutate({ reportId, status: newStatus });
  };

  const handleExport = (format: 'csv' | 'json') => {
    const data = proposals.map((p) => ({
      title: p.title,
      client: p.clientName,
      status: p.status,
      value: p.value,
      deadline: p.deadline,
      reviewer: p.currentReviewer,
      lastUpdated: p.lastUpdated,
    }));

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stage-gate-checklist-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'csv') {
      const headers = ['Title', 'Client', 'Status', 'Value', 'Deadline', 'Reviewer', 'Last Updated'];
      const rows = data.map((d) => [
        d.title,
        d.client,
        d.status,
        d.value?.toString() || '',
        d.deadline || '',
        d.reviewer || '',
        d.lastUpdated || '',
      ]);
      const csv = [headers, ...rows].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stage-gate-checklist-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      clientName: '',
      dateRange: undefined,
      reviewer: '',
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto p-4 md:p-6">
        <DashboardSkeleton />
      </div>
    );
  }

  if (!projectId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <p className="text-center py-12 text-gray-500">
            Select a project to view stage gate checklist
          </p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 md:p-6">
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">Error loading proposals: {(error as Error).message}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Stage Gate Checklist</h1>
          <p className="text-gray-600 mt-1">
            Track proposals through review stages with kanban visualization
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative group">
            <Button variant="outline" onClick={() => handleExport('csv')}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <div className="hidden group-hover:block absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[140px]">
              <button
                onClick={() => handleExport('csv')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
              >
                Export as CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
              >
                Export as JSON
              </button>
            </div>
          </div>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </h3>
          {(filters.status !== 'all' ||
            filters.clientName ||
            filters.dateRange ||
            filters.reviewer) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Status</label>
            <Select
              value={filters.status || 'all'}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value as StageGateStatus | 'all' })
              }
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="submitted">Submitted</option>
              <option value="archived">Archived</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Client Name</label>
            <Input
              type="text"
              placeholder="Filter by client..."
              value={filters.clientName || ''}
              onChange={(e) => setFilters({ ...filters, clientName: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Reviewer</label>
            <Input
              type="text"
              placeholder="Filter by reviewer..."
              value={filters.reviewer || ''}
              onChange={(e) => setFilters({ ...filters, reviewer: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Date Range</label>
            <Select
              value={filters.dateRange || 'all'}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  dateRange: e.target.value === 'all' ? undefined : (e.target.value as '7d' | '30d' | '90d'),
                })
              }
            >
              <option value="all">All Time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Kanban Board */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-theme-text flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Proposal Pipeline
          </h2>
          <div className="text-sm text-theme-text-secondary">
            {proposals.length} proposal{proposals.length !== 1 ? 's' : ''} total
          </div>
        </div>
        <StageGateKanban 
          proposals={proposals} 
          onProposalClick={handleProposalClick}
          onStatusChange={handleStatusChange}
          canChangeStatus={userRole !== 'viewer'}
          userRole={userRole}
        />
      </Card>
    </div>
  );
}
